import { useState, useCallback, useMemo } from "react";
import Model from "react-body-highlighter";
import ChartTooltip from "./ChartTooltip.jsx";
import { anteriorData, posteriorData } from "../utils/bodyMapData.js";
import { LIBRARY_MUSCLE_LABELS, buildMuscleStats, signed } from "../utils/muscleStats.js";
import styles from "./BodyMap.module.css";

const AMBER_RAMP = [
  "rgba(217,126,36,0.25)",
  "rgba(217,126,36,0.42)",
  "rgba(217,126,36,0.58)",
  "rgba(217,126,36,0.75)",
  "rgba(217,126,36,0.92)",
];
const BODY_COLOR = "#DFD6C6";
const SVG_STYLE = { borderRadius: 8 };

function buildModelData(muscleCounts) {
  if (!muscleCounts?.length) return [];
  const maxCount = Math.max(...muscleCounts.map((m) => m.setCount), 1);
  const entries = [];
  for (const { muscle, setCount } of muscleCounts) {
    if (setCount === 0) continue;
    const freq = Math.max(1, Math.round((setCount / maxCount) * 5));
    for (let i = 0; i < freq; i++) {
      entries.push({ name: `${muscle}-${i}`, muscles: [muscle] });
    }
  }
  return entries;
}

// Percent is meaningless when the previous period had no sets at all, so a
// 0 → n jump reads as "new" instead.
function deltaPercent(setCount, prevSetCount) {
  if (prevSetCount === 0) return setCount > 0 ? "new" : null;
  return `${signed(Math.round(((setCount - prevSetCount) / prevSetCount) * 100))}%`;
}

function BodyView({ type, modelData, stats, overlayData }) {
  const [tooltip, setTooltip] = useState(null);
  const [pos, setPos] = useState({ x: 0, y: 0 });

  // A muscle with nothing on either side of the period still answers: the
  // region is drawn and takes a cursor, so staying silent read as broken.
  const handleEnter = useCallback((muscle, e) => {
    const entry = stats.get(muscle) || { setCount: 0, prevSetCount: 0, delta: 0 };
    setTooltip({ label: LIBRARY_MUSCLE_LABELS[muscle], ...entry });
    setPos({ x: e.clientX, y: e.clientY });
  }, [stats]);

  const handleMove = useCallback((e) => {
    if (tooltip) setPos({ x: e.clientX, y: e.clientY });
  }, [tooltip]);

  const handleLeave = useCallback(() => setTooltip(null), []);

  const percent = tooltip ? deltaPercent(tooltip.setCount, tooltip.prevSetCount) : null;

  return (
    <div className={styles.view}>
      <div className={styles.modelWrap}>
        <Model
          data={modelData}
          bodyColor={BODY_COLOR}
          highlightedColors={AMBER_RAMP}
          type={type}
          svgStyle={SVG_STYLE}
          style={{ width: "100%" }}
        />

        <svg
          className={styles.overlay}
          viewBox="0 0 100 200"
          aria-hidden="true"
        >
          {/* `head` and `knees` are not muscles and the soleus slivers have no
              counterpart in the data — drawn, they would only hand the cursor a
              region that can never answer. */}
          {overlayData.filter(({ muscle }) => LIBRARY_MUSCLE_LABELS[muscle]).map(({ muscle, svgPoints }) => svgPoints.map((points, i) => (
            <polygon
              key={`${muscle}-${i}`}
              data-muscle={muscle}
              points={points}
              fill="transparent"
              onMouseEnter={(e) => handleEnter(muscle, e)}
              onMouseMove={handleMove}
              onMouseLeave={handleLeave}
            />
          )))}
        </svg>
      </div>

      {tooltip && (
        <ChartTooltip className={styles.tooltip} x={pos.x} y={pos.y}>
          <span className={styles.tooltipLabel}>{tooltip.label}</span>
          <span className={styles.tooltipValue}>{tooltip.setCount} sets</span>
          <span className={styles.tooltipPrev}>
            prev {tooltip.prevSetCount}
            {tooltip.delta !== 0 && (
              <span className={tooltip.delta > 0 ? styles.up : styles.down}>
                {" "}{signed(tooltip.delta)}{percent ? ` (${percent})` : ""}
              </span>
            )}
          </span>
        </ChartTooltip>
      )}
    </div>
  );
}

export default function BodyMap({ muscleCounts, prevMuscleCounts }) {
  const counts = useMemo(() => muscleCounts || [], [muscleCounts]);

  const stats = useMemo(
    () => buildMuscleStats(counts, prevMuscleCounts),
    [counts, prevMuscleCounts]
  );

  const modelData = buildModelData(counts);

  return (
    <div id="muscle-body-map" className={styles.map}>
      <div className={styles.views}>
        <BodyView type="anterior" modelData={modelData} stats={stats} overlayData={anteriorData} />
        <BodyView type="posterior" modelData={modelData} stats={stats} overlayData={posteriorData} />
      </div>
    </div>
  );
}
